import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { OrcSettings } from 'app/model/dataflow/dataset-settings/orc-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(OrcSettings)
})
export class OrcFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: '${DatasetFormatType.Orc}'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`orcCompressionCodec: '${compressionCodec}'`);
        }
    }

    protected async toSourceDSL(orcSettings: OrcSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(orcSettings)));
        if (orcSettings.compressionType) {
            props.push(`orcCompressionCodec: '${orcSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSinkDSL(orcSettings: OrcSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSinkDSL(orcSettings)));
        if (orcSettings.compressionType) {
            props.push(`orcCompressionCodec: '${orcSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<OrcSettings> {
        const orcSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as OrcSettings;
        if (configs.has('orcCompressionCodec')) {
            orcSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('orcCompressionCodec')
            );
            handledConfigs.push('orcCompressionCodec');
        }
        return orcSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<OrcSettings> {
        const orcSettings = (await super.toSinkModel(owner, configs, handledConfigs)) as OrcSettings;
        if (configs.has('orcCompressionCodec')) {
            orcSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('orcCompressionCodec')
            );
            handledConfigs.push('orcCompressionCodec');
        }
        return orcSettings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): OrcSettings {
        return Owned.create<OrcSettings>(OrcSettings.MODEL, owner);
    }
}
